import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import {
  setConfirmHandler,
  subscribeSessions,
  endAllSessions,
  describePayload,
  CONFIRM_WINDOW_MINUTES,
} from '../../utils/criticalChange';

/**
 * 핵심 값(단가·마진·환율·정산·배송비) 변경 확인 창 + 편집 중 경고 띠.
 *
 * 저장 요청이 나가기 직전에 axios 가 이 창을 띄운다(src/utils/criticalChange.js).
 * 사유를 적고 「확인했습니다」를 체크해야 저장된다. 확인은 같은 종류의 변경에 한해
 * 몇 분 동안 유효하고, 그동안 화면 위에 경고 띠가 떠 있다 — 자동 저장 화면에서
 * 한 글자마다 창이 뜨지 않게 하면서도, 지금 핵심 값을 고치는 중이라는 것은 계속 보이게 한다.
 * 서버는 확인 없는 요청을 거부하고(enforce 모드), 확인 여부·사유를 모두 기록한다.
 */
export default function CriticalChangeProvider({ children }) {
  const [pending, setPending] = useState(null); // { info, resolve }
  const [reason, setReason] = useState('');
  const [sessions, setSessions] = useState([]);
  const [, tick] = useState(0);
  const resolveRef = useRef(null);
  /* 창이 뜰 때 초점을 뒤의 드롭다운·입력칸에서 빼내 사유 칸으로 옮기고, 확인 단추는 잠깐 뒤에 연다 — 2026-10-16 점검:
     이 창은 드롭다운을 고르거나 Enter·칸 벗어남으로 저장할 때 뜨는데, 초점이 뒤에 남아 방향키를 누르면 값이 또 바뀌어
     같이 저장될 수 있었다(같은 날 배송 상태 확인창 사고 후속). */
  const reasonRef = useRef(null);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    setConfirmHandler(
      (info) =>
        new Promise((resolve) => {
          resolveRef.current = resolve;
          try { if (document.activeElement?.blur) document.activeElement.blur(); } catch (e) { /* 무시 */ }
          setReason('');
          setArmed(process.env.NODE_ENV === 'test'); // 시험에서는 기다리지 않는다
          setPending(info);
        })
    );
    const off = subscribeSessions(setSessions);
    const timer = setInterval(() => tick((n) => n + 1), 15000); // 남은 시간 표시만 새로 그린다
    return () => {
      setConfirmHandler(null);
      off();
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!pending) return undefined;
    const f = setTimeout(() => reasonRef.current?.focus?.(), 0);
    const t = setTimeout(() => setArmed(true), process.env.NODE_ENV === 'test' ? 0 : 600);
    return () => { clearTimeout(f); clearTimeout(t); };
  }, [pending]);

  const close = useCallback((result) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setPending(null);
    if (resolve) resolve(result);
  }, []);

  /* 사유·확인란은 선택(2026-10-07 대표님 «사유 입력 안 해도 확인 버튼 누르면 처리되게»).
     해외 KOL팀이 이유 칸을 못 채워 버튼이 꺼진 채 막혔다. 서버는 사유 2글자 이상을 요구하므로(critical-routes.ts)
     비우면 «사유 미입력»을 대신 실어 보낸다 — 기록에는 그대로 «사유 미입력»으로 남는다. */
  const NO_REASON = '사유 미입력';
  const finalReason = reason.trim().length >= 2 ? reason.trim() : NO_REASON;
  const rows = pending ? describePayload(pending.data, pending.fields) : [];
  const now = Date.now();
  const live = sessions.filter((s) => s.expiresAt > now);

  return (
    <>
      {/* 감싼 앱 화면. 이게 빠져서 2026-09-30 10:19 배포 뒤 모든 화면이 비었다 */}
      {children}
      {live.length > 0 && (
        <div
          role="alert"
          className="fixed top-0 inset-x-0 z-[1000] flex items-center justify-center gap-3 bg-[#8A4A40] px-4 py-1.5 text-xs text-white"
        >
          <span className="font-bold">핵심 값 편집 중</span>
          <span>
            {live.map((s) => `${s.label}(${Math.max(1, Math.ceil((s.expiresAt - now) / 60000))}분 남음)`).join(' · ')}
          </span>
          <span className="opacity-80">— 저장하는 값은 단가·정산 계산에 바로 반영되고 서버에 기록됩니다</span>
          <button type="button" className="ml-2 rounded border border-white/60 px-2 py-0.5" onClick={endAllSessions}>
            지금 끝내기
          </button>
        </div>
      )}

      <Modal isOpen={!!pending} onClose={() => close(null)} title="" size="sm">
        {pending && (
          <div>
            {/* 해외 KOL팀이 이 창에서 막혔다(2026-10-07 말레이시아 Ruth) — 창이 Weglot 번역 범위 밖이고 새 문구는 번역도 안 왔다.
                영어를 직접 함께 적고, Weglot 이 다시 건드리지 않게 data-wg-notranslate 를 단다 */}
            <h3 className="text-md font-bold text-text-dark text-center mb-1">핵심 값을 바꾸시겠습니까?
              <span data-wg-notranslate className="block text-xs font-normal text-gray-400">Change a key value?</span>
            </h3>
            <p className="text-sm text-center text-text-dark mb-1">{pending.label}</p>
            <p className="text-xs text-[#E0806F] text-center mb-3">
              저장하면 단가·마진·정산 계산에 바로 반영됩니다. 누가 언제 무엇을 왜 바꿨는지 서버에 기록됩니다.
              <span data-wg-notranslate className="block mt-0.5 text-gray-400">This affects price, margin and settlement. Who changed what, when and why is logged.</span>
            </p>

            {pending.warning && (
              <p className="mb-3 rounded-md border border-[#8A4A40] bg-[#2A1C1A] px-3 py-2 text-xs text-[#F0B0A0]">
                ⚠ {pending.warning}
              </p>
            )}

            {/* 수정을 다 마치고 저장할 때(askCriticalConfirm) — 요청 본문이 아니라 «바뀐 값만 이전 → 이후»로 보여 준다 */}
            {pending.summary?.length > 0 && (
              <div className="mb-3 max-h-56 overflow-auto rounded-md bg-[#2A2A2A] px-3 py-2 text-xs" data-testid="change-summary">
                <p className="mb-1 text-gray-400">바뀐 값 {pending.summary.length}건</p>
                {pending.summary.map((r, i) => (
                  <div key={i} className="flex justify-between gap-3 border-t border-[#3a3a3a] py-1 first:border-t-0">
                    <span className="text-gray-400 break-all">
                      {r.scope && <span className="text-gray-500">{r.scope} · </span>}{r.label}
                    </span>
                    <span className="font-bold text-text-dark text-right shrink-0">
                      {r.before !== undefined && <span className="font-normal text-gray-500">{r.before} → </span>}{r.after}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {rows.length > 0 && (
              <div className="mb-3 max-h-40 overflow-auto rounded-md bg-[#2A2A2A] px-3 py-2 text-xs">
                <p className="mb-1 text-gray-400">저장될 값</p>
                {rows.map((r) => (
                  <div key={r.key} className="flex justify-between gap-3">
                    <span className="text-gray-400">{r.label}</span>
                    <span className="font-bold text-text-dark break-all text-right">{r.value}</span>
                  </div>
                ))}
                {pending.more > 0 && <p className="mt-1 text-gray-500">외 {pending.more}건</p>}
              </div>
            )}

            <label className="block text-xs text-gray-600 mb-1" htmlFor="critical-reason">
              바꾸는 이유 (선택 · 기록에 남습니다) <span data-wg-notranslate>/ Reason (optional)</span>
            </label>
            <input
              ref={reasonRef}
              id="critical-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="예: 9월 환율 반영 / e.g. KOL list upload"
              autoComplete="off"
              maxLength={200}
              className="w-full rounded-md border border-border-gray px-3 py-2 text-xs mb-2"
            />
            {pending.once ? (
              <p className="text-[11px] text-gray-500 mb-4">
                확인하면 위 내용이 한 번에 저장됩니다. 취소하면 저장하지 않고, 고친 값은 화면에 그대로 남습니다.
                <span data-wg-notranslate className="block">Confirming saves all the changes above at once. Cancel keeps your edits on screen without saving.</span>
              </p>
            ) : (
              <p className="text-[11px] text-gray-500 mb-4">
                확인하면 {CONFIRM_WINDOW_MINUTES}분 동안 같은 종류의 변경은 다시 묻지 않고, 화면 위에 경고 띠가 표시됩니다.
                <span data-wg-notranslate className="block">After confirming, you won't be asked again for {CONFIRM_WINDOW_MINUTES} minutes.</span>
              </p>
            )}

            <div className="flex gap-3">
              <Button
                variant="black"
                className="flex-1 !text-sm"
                onClick={() => { if (armed) close({ reason: finalReason }); }}
                disabled={!armed}
              >
                확인하고 저장 <span data-wg-notranslate>/ Save</span>
              </Button>
              <Button variant="outline" className="flex-1 !text-sm" onClick={() => close(null)}>
                취소 <span data-wg-notranslate>/ Cancel</span>
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
